// This file contains the main app logic that was previously in App.tsx
// Now it will be wrapped with authentication in the new App.tsx

import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Sidebar } from './Sidebar';
import UpgradeModal from './UpgradeModal';
import WelcomeTour from './WelcomeTour';
import WelcomeModal from './WelcomeModal';
import ConfirmDialog from './ConfirmDialog';
import ProfilePage from './ProfilePage';
import OnboardingProfileDialog from './OnboardingProfileDialog';
import AdminDashboard from './AdminDashboard';
import SuperAdminDashboard from './SuperAdminDashboard';
import { useLocalizedTests } from '../hooks/useLocalizedTests';
import { ViewState, User, TestResult, Clinician, UserRole } from '../types';
import { v4 as uuidv4 } from 'uuid';

// Lazy load heavy components for code splitting
const TestTaker = lazy(() => import('./TestTaker').then(m => ({ default: m.TestTaker })));
const ReportView = lazy(() => import('./ReportView').then(m => ({ default: m.ReportView })));
const PatientsList = lazy(() => import('./PatientsList'));
const PatientProfile = lazy(() => import('./PatientProfile'));
const EvolutivosList = lazy(() => import('./EvolutivosList').then(m => ({ default: m.EvolutivosList })));
const EvolutivoDetail = lazy(() => import('./EvolutivoDetail').then(m => ({ default: m.EvolutivoDetail })));
const CompareTestsView = lazy(() => import('./CompareTestsView'));
const UsersManagement = lazy(() => import('./UsersManagement'));
const SubscriptionManager = lazy(() => import('./SubscriptionManager'));
const Settings = lazy(() => import('./Settings').then(m => ({ default: m.Settings })));
const TestListView = lazy(() => import('./views').then(m => ({ default: m.TestListView })));
const DashboardView = lazy(() => import('./views').then(m => ({ default: m.DashboardView })));
const HistoryView = lazy(() => import('./views').then(m => ({ default: m.HistoryView })));
const BatteryList = lazy(() => import('./batteries/BatteryList'));
const BatteryRunner = lazy(() => import('./batteries/BatteryRunner'));
const BatteryReport = lazy(() => import('./batteries/BatteryReport'));

// Loading fallback for lazy components
const ViewLoader = () => {
  const { t } = useTranslation('common');
  return (
    <div className="flex items-center justify-center h-full min-h-[400px]">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
        <p className="text-slate-500 text-sm">{t('loading')}</p>
      </div>
    </div>
  );
};
import { createTestResult, updateTestResult, getTestResultById, deleteTestResult } from '../services/testResultService';
import { getPatientById } from '../services/patientService';
import { TestBattery } from '../types';
import BatteryStartModal from './batteries/BatteryStartModal';
import { createAlerts } from '../services/alertService';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentClinician } from '../hooks/useCurrentClinician';
import { useUsageLimits } from '../hooks/useUsageLimits';
import { useResultsFilter } from '../hooks/useResultsFilter';
import { useEvolutivos } from '../hooks/useEvolutivos';
import { useAlerts } from '../hooks/useAlerts';
import { useAppData } from '../hooks/useAppData';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { evaluateAlerts } from '../utils/alertEvaluator';
import AlertsPanel from './AlertsPanel';

export default function MainApp() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation(['common', 'app']);
  const { user, signOut } = useAuth();
  const { tests: TEST_DEFINITIONS } = useLocalizedTests();
  const { clinician: initialClinician, loading: loadingClinician } = useCurrentClinician();
  const [currentClinician, setCurrentClinician] = useState<Clinician | null>(null);
  const { canTakeTest, testsTaken, testsLimit, subscriptionTier, refresh: refreshLimits } = useUsageLimits();
  const { evolutivos } = useEvolutivos();
  const {
    alerts,
    unreadCount: alertUnreadCount,
    criticalCount: alertCriticalCount,
    markAllRead: handleMarkAllAlertsRead,
    dismiss: handleDismissAlert,
    dismissAll: handleDismissAllAlerts,
  } = useAlerts(currentClinician?.id ?? undefined);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [onboardingCompleted, setOnboardingCompleted] = useLocalStorage<boolean>('onboardingCompleted', false);
  const [, setWelcomeTourCompleted] = useLocalStorage<boolean>('welcomeTourCompleted', false);

  // Consolidated data loading hook (solves N+1 query problem)
  const {
    results,
    patientsMap,
    loading: loadingData,
    refreshResults,
    setResults,
  } = useAppData(user);

  const [currentView, setCurrentView] = useState<ViewState>('DASHBOARD');
  const [pendingPatientId, setPendingPatientId] = useState<string | null>(null);
  const [pendingBattery, setPendingBattery] = useState<TestBattery | null>(null);
  const [activeResultData, setActiveResultData] = useState<TestResult | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeModalLimitReached, setUpgradeModalLimitReached] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Sync local clinician state with hook
  useEffect(() => {
    if (initialClinician) {
      setCurrentClinician(initialClinician);
    }
  }, [initialClinician]);

  // Apply dashboard-active class to body on mount (for mobile scroll prevention)
  useEffect(() => {
    document.body.classList.add('dashboard-active');
    return () => {
      document.body.classList.remove('dashboard-active');
    };
  }, []);

  // Check if user has completed onboarding
  useEffect(() => {
    if (!currentClinician || onboardingCompleted) {
      return;
    }

    const createdAt = new Date(currentClinician.createdAt).getTime();
    const ageInDays = (Date.now() - createdAt) / (1000 * 60 * 60 * 24);
    const shouldShowOnboarding = Number.isFinite(createdAt) && ageInDays <= 7;

    if (!shouldShowOnboarding) {
      return;
    }

    // Show welcome modal after 500ms delay
    const timer = setTimeout(() => {
      setShowWelcomeModal(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [currentClinician]);

  // Results filter hook
  const {
    filters,
    filteredResults,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    uniquePatients,
    uniqueTests,
    uniqueCategories,
  } = useResultsFilter(results);

  useEffect(() => {
    if (typeof currentView === 'object' && currentView.type === 'VIEW_REPORT') {
      loadActiveResult(currentView.resultId);
    } else {
      setActiveResultData(null);
    }
  }, [currentView]);

  // Handle Stripe redirect params (?success=true after checkout, ?tab=subscription after portal)
  useEffect(() => {
    const success = searchParams.get('success');
    const tab = searchParams.get('tab');
    if (success === 'true') {
      setCurrentView('SUBSCRIPTION');
      refreshLimits();
      toast.success(t('app:subscription.paymentSuccess', '¡Suscripción activada correctamente!'));
      setSearchParams({}, { replace: true });
    } else if (tab === 'subscription') {
      setCurrentView('SUBSCRIPTION');
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh usage limits whenever the user navigates to the test list.
  // This breaks the deadlock where a user blocked in month N can never trigger
  // a refresh (refreshLimits only fires after completing a test, not before).
  useEffect(() => {
    if (currentView === 'TEST_LIST' || currentView === 'PATIENTS_LIST') {
      refreshLimits();
    }
  }, [currentView]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadActiveResult = async (resultId: string) => {
    const result = await getTestResultById(resultId);
    if (result) {
      setActiveResultData(result);
    } else {
      const localResult = results.find(r => r.id === resultId);
      setActiveResultData(localResult || null);
    }
  };

  const activeTest = typeof currentView === 'object' && currentView.type === 'TAKE_TEST'
    ? TEST_DEFINITIONS.find(t => t.id === currentView.testId) || null
    : null;

  const activeResultTest = activeResultData
    ? TEST_DEFINITIONS.find(t => t.code === activeResultData.testCode) || null
    : null;

  const handleCompleteTest = async (partialResult: Omit<TestResult, 'id' | 'severityLabel' | 'aiAnalysis'>) => {
    let patientName = '';
    if (partialResult.patientId) {
      const patient = await getPatientById(partialResult.patientId);
      if (patient) {
        patientName = patient.name;
      }
    }

    const testDef = TEST_DEFINITIONS.find(t => t.code === partialResult.testCode);
    const interpretation = testDef?.interpretations.find(
      i => partialResult.totalScore >= i.min && partialResult.totalScore <= i.max
    );
    const severityLabel = interpretation?.label || 'Indefinido';

    const newResultData = {
      ...partialResult,
      userId: user?.id, // Add user_id from auth context
      severityLabel,
      patientName,
    };

    const savedResult = await createTestResult(newResultData);
    if (savedResult) {
      // Evaluate and create clinical alerts
      if (testDef && currentClinician) {
        const triggered = evaluateAlerts(testDef, savedResult);
        if (triggered.length > 0) {
          await createAlerts(triggered.map((ta) => ({
            clinicianId: currentClinician.id,
            patientId: partialResult.patientId || null,
            testResultId: savedResult.id,
            testCode: partialResult.testCode,
            patientName,
            severity: ta.threshold.severity,
            message: ta.threshold.message,
            triggeredScore: ta.triggeredScore,
          })));
        }
      }
      await refreshResults();
      refreshLimits(); // Refresh usage limits after creating test result
      setCurrentView({ type: 'VIEW_REPORT', resultId: savedResult.id });
    } else {
      // Fallback to localStorage if Supabase fails
      const newResult: TestResult = {
        id: uuidv4(),
        ...newResultData,
      };
      setResults([newResult, ...results]);
      refreshLimits(); // Refresh limits even in fallback mode
      setCurrentView({ type: 'VIEW_REPORT', resultId: newResult.id });
    }
  };

  const handleUpdateResult = async (updatedResult: TestResult) => {
    const success = await updateTestResult(updatedResult.id, updatedResult);
    if (success) {
      setResults(results.map(r => r.id === updatedResult.id ? updatedResult : r));
      setActiveResultData(updatedResult);
    } else {
      setResults(results.map(r => r.id === updatedResult.id ? updatedResult : r));
      setActiveResultData(updatedResult);
    }
  };

  const handleDeleteResult = (resultId: string, resultInfo: { testCode: string; patientName?: string; date: string }) => {
    setConfirmDialog({
      isOpen: true,
      title: t('app:reports.deleteReport'),
      message: t('app:reports.confirmDeleteMessage', { testCode: resultInfo.testCode, patientName: resultInfo.patientName || t('common:labels.anonymous'), date: new Date(resultInfo.date).toLocaleDateString() }),
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        const loadingToast = toast.loading(t('app:reports.deleting'));

        // Optimistic update: Remove from UI immediately
        const previousResults = results;
        setResults(results.filter(r => r.id !== resultId));

        try {
          const success = await deleteTestResult(resultId);

          if (success) {
            await refreshResults();
            refreshLimits();
            toast.success(t('app:reports.deleteSuccess'), { id: loadingToast });
          } else {
            // Rollback optimistic update
            setResults(previousResults);
            toast.error(t('app:toasts.errorDelete'), { id: loadingToast });
          }
        } catch (error) {
          // Rollback optimistic update
          setResults(previousResults);
          toast.error(t('app:toasts.errorDelete'), { id: loadingToast });
        }
      },
    });
  };

  const handleExportData = () => {
    try {
      const dataStr = JSON.stringify(results, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `psychometric-pro-backup-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(t('app:history.exportError'));
    }
  };

  const handleImportData = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/json';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const imported = JSON.parse(event.target?.result as string);
          if (Array.isArray(imported)) {
            setResults(imported);
            toast.success(t('app:history.importSuccess', { count: imported.length }));
          } else {
            toast.error(t('app:history.invalidFormat'));
          }
        } catch (error) {
          console.error('Error importing data:', error);
          toast.error(t('app:history.importError'));
        }
      };
      reader.readAsText(file);
    };
    input.click();
  };

  const handleClearAllData = () => {
    setConfirmDialog({
      isOpen: true,
      title: t('app:history.deleteAll'),
      message: t('app:history.deleteAllConfirm'),
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        setResults([]);
        toast.success(t('app:history.allDataDeleted'));
      },
    });
  };

  const renderContent = () => {
    // Guard: redirect evolutivos views if user lacks access
    if (!canAccessEvolutivos) {
      if (
        currentView === 'EVOLUTIVOS' ||
        (typeof currentView === 'object' && (currentView.type === 'EVOLUTIVO_DETAIL' || currentView.type === 'CREATE_EVOLUTIVO'))
      ) {
        setCurrentView('DASHBOARD');
        return null;
      }
    }

    if (typeof currentView === 'object') {
      if (currentView.type === 'TAKE_TEST' && activeTest) {
        return (
          <Suspense fallback={<ViewLoader />}>
            <div key={`take-test-${currentView.testId}`}>
              <TestTaker
                test={activeTest}
                patientId={currentView.patientId}
                onCancel={() => setCurrentView('TEST_LIST')}
                onComplete={handleCompleteTest}
              />
            </div>
          </Suspense>
        );
      }
      if (currentView.type === 'VIEW_REPORT' && activeResultData && activeResultTest) {
        return (
          <Suspense fallback={<ViewLoader />}>
            <div key={`view-report-${currentView.resultId}`}>
              <ReportView
                result={activeResultData}
                test={activeResultTest}
                onBack={() => setCurrentView('HISTORY')}
                onUpdateResult={handleUpdateResult}
                onDelete={refreshResults}
                clinician={currentClinician}
              />
            </div>
          </Suspense>
        );
      }
      if (currentView.type === 'PATIENT_PROFILE') {
        return (
          <Suspense fallback={<ViewLoader />}>
            <div key={`patient-profile-${currentView.patientId}`}>
              <PatientProfile
                patientId={currentView.patientId}
                onBack={() => setCurrentView('PATIENTS_LIST')}
                onViewReport={(resultId) => setCurrentView({ type: 'VIEW_REPORT', resultId })}
                onTakeTest={(patientId) => {
                  if (!canTakeTest) {
                    toast.error(t('app:tests.limitReachedUpgrade'));
                    setUpgradeModalLimitReached(true);
                    setShowUpgradeModal(true);
                    return;
                  }
                  setPendingPatientId(patientId);
                  toast.success(t('app:tests.patientPreselected'));
                  setCurrentView('TEST_LIST');
                }}
                onCompareTests={(test1Id, test2Id) => setCurrentView({ type: 'COMPARE_TESTS', test1Id, test2Id })}
              />
            </div>
          </Suspense>
        );
      }
      if (currentView.type === 'EVOLUTIVO_DETAIL') {
        return (
          <Suspense fallback={<ViewLoader />}>
            <div key={`evolutivo-detail-${currentView.evolutivoId}`}>
              <EvolutivoDetail
                evolutivoId={currentView.evolutivoId}
                onNavigate={setCurrentView}
              />
            </div>
          </Suspense>
        );
      }
      if (currentView.type === 'COMPARE_TESTS') {
        return (
          <Suspense fallback={<ViewLoader />}>
            <div key={`compare-tests-${currentView.test1Id}-${currentView.test2Id}`}>
              <CompareTestsView
                test1Id={currentView.test1Id}
                test2Id={currentView.test2Id}
                onBack={(patientId) =>
                  patientId
                    ? setCurrentView({ type: 'PATIENT_PROFILE', patientId })
                    : setCurrentView('PATIENTS_LIST')
                }
              />
            </div>
          </Suspense>
        );
      }
      if (currentView.type === 'BATTERY_RUNNER') {
        return (
          <Suspense fallback={<ViewLoader />}>
            <div key={`battery-runner-${currentView.sessionId}`}>
              <BatteryRunner
                sessionId={currentView.sessionId}
                clinicianId={currentClinician?.id ?? ''}
                userId={user?.id}
                onNavigate={setCurrentView}
                onAbandon={() => setCurrentView('BATTERIES')}
              />
            </div>
          </Suspense>
        );
      }
      if (currentView.type === 'BATTERY_REPORT') {
        return (
          <Suspense fallback={<ViewLoader />}>
            <div key={`battery-report-${currentView.sessionId}`}>
              <BatteryReport
                sessionId={currentView.sessionId}
                onNavigate={setCurrentView}
                onBack={() => setCurrentView('BATTERIES')}
              />
            </div>
          </Suspense>
        );
      }
    }

    if (currentView === 'BATTERIES') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="batteries-view">
            <BatteryList
              clinicianId={currentClinician?.id ?? ''}
              onNavigate={setCurrentView}
              onStartBattery={(battery) => setPendingBattery(battery)}
            />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'PATIENTS_LIST') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="patients-list-view">
            <PatientsList
              onViewPatient={(patientId) => setCurrentView({ type: 'PATIENT_PROFILE', patientId })}
            />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'EVOLUTIVOS') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="evolutivos-view">
            <EvolutivosList
              onNavigate={setCurrentView}
            />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'USERS_MANAGEMENT') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="users-management-view">
            <UsersManagement currentUser={currentClinician} />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'SUBSCRIPTION') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="subscription-view">
            <SubscriptionManager />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'SETTINGS') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="settings-view">
            <Settings />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'ADMIN_DASHBOARD') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="admin-dashboard-view">
            <AdminDashboard />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'SUPERADMIN_DASHBOARD') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="superadmin-dashboard-view">
            <SuperAdminDashboard />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'PROFILE') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="profile-view">
            <ProfilePage
              clinician={currentClinician}
              results={results}
              onUpdate={handleUpdateClinician}
            />
          </div>
        </Suspense>
      );
    }

    if (currentView === 'TEST_LIST') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="test-list-view">
            <TestListView
              subscriptionTier={subscriptionTier}
              canTakeTest={canTakeTest}
              pendingPatientId={pendingPatientId}
              onNavigate={setCurrentView}
              onClearPendingPatient={() => setPendingPatientId(null)}
              onShowUpgradeModal={(limitReached) => {
                setUpgradeModalLimitReached(limitReached);
                setShowUpgradeModal(true);
              }}
            />
          </div>
        </Suspense>
      );
    }

    // Dashboard
    if (currentView === 'DASHBOARD') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="dashboard-view">
            <DashboardView
              results={results}
              evolutivos={evolutivos}
              patientsMap={patientsMap}
              loadingData={loadingData}
              onNavigate={setCurrentView}
              onShowUpgradeModal={() => {
                setUpgradeModalLimitReached(false);
                setShowUpgradeModal(true);
              }}
            />
          </div>
        </Suspense>
      );
    }

    // History view
    if (currentView === 'HISTORY') {
      return (
        <Suspense fallback={<ViewLoader />}>
          <div key="history-view">
            <HistoryView
              results={results}
              filteredResults={filteredResults}
              loadingData={loadingData}
              filters={filters}
              hasActiveFilters={hasActiveFilters}
              uniquePatients={uniquePatients}
              uniqueTests={uniqueTests}
              uniqueCategories={uniqueCategories}
              onFilterChange={updateFilter}
              onClearFilters={clearFilters}
              onNavigate={setCurrentView}
              onDeleteResult={handleDeleteResult}
              onImportData={handleImportData}
              onClearAllData={handleClearAllData}
            />
          </div>
        </Suspense>
      );
    }

    // Default return (should not reach here)
    return null;
  };

  // Show loading while clinician data is being fetched
  if (loadingClinician || !currentClinician) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-slate-600">{t('common:loadingProfile')}</p>
        </div>
      </div>
    );
  }

  // currentClinician is guaranteed non-null from here on
  const canAccessEvolutivos = (currentClinician.evolutivosEnabled ?? false) || currentClinician.role === UserRole.SUPERADMIN;

  // Convert Clinician to User type for Sidebar compatibility
  const currentUser: User = {
    id: currentClinician.id,
    name: currentClinician.name,
    role: currentClinician.role,
    avatarUrl: currentClinician.avatarUrl,
  };

  const handleCompleteWelcomeModal = () => {
    setShowWelcomeModal(false);
    setOnboardingCompleted(true);
    // After welcome modal, show the tour after a short delay
    setTimeout(() => {
      setShowWelcomeTour(true);
    }, 500);
  };

  const handleSkipWelcomeModal = () => {
    setShowWelcomeModal(false);
    setOnboardingCompleted(true);
    // Don't show tour if they skipped the welcome modal
  };

  const handleCompleteTour = () => {
    setShowWelcomeTour(false);
    setWelcomeTourCompleted(true);
  };

  const handleSkipTour = () => {
    setShowWelcomeTour(false);
    setWelcomeTourCompleted(true);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const handleUpdateClinician = (updatedClinician: Clinician) => {
    setCurrentClinician(updatedClinician);
  };

  // Show onboarding if clinician_type not set (not applicable to SUPERADMIN)
  const needsOnboarding = currentClinician.role !== UserRole.SUPERADMIN && !currentClinician.clinicianType;

  return (
    <div className="h-screen bg-slate-50 overflow-hidden lg:overflow-auto">
      {/* Onboarding profile dialog — blocks app until completed */}
      {needsOnboarding && (
        <OnboardingProfileDialog
          clinician={currentClinician}
          onComplete={(updated) => setCurrentClinician(updated)}
        />
      )}

      {/* Skip link for accessibility - visible only on focus */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-blue-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg focus:shadow-lg focus:outline-none"
      >
        {t('common:navigation.skipToContent')}
      </a>

      <Sidebar
        currentView={currentView}
        onNavigate={setCurrentView}
        user={currentUser}
        onLogout={handleLogout}
        evolutivosEnabled={canAccessEvolutivos}
        alertCount={alertUnreadCount}
        criticalCount={alertCriticalCount}
        onOpenAlerts={() => setShowAlertsPanel(true)}
      />

      {/* Sticky Header for Mobile - Very thin and compact */}
      <div className="lg:hidden sticky top-0 z-30 bg-gradient-to-r from-slate-900 to-slate-800 text-white shadow-md no-print">
        <div className="flex items-center justify-center py-1.5 px-3">
          <div className="flex items-center gap-2">
            <img src="/favicon.png" alt="Psymetrika" className="w-5 h-5 rounded-md" />
            <span className="text-sm font-bold tracking-tight text-blue-400">Psymetrika</span>
          </div>
        </div>
      </div>

      <main id="main-content" className="h-full px-2 py-2 pb-20 pt-2 lg:pt-2 lg:ml-64 lg:p-8 lg:pb-8 overflow-y-auto">{renderContent()}</main>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        testsTaken={testsTaken}
        testsLimit={testsLimit}
        limitReached={upgradeModalLimitReached}
      />

      {/* Welcome Modal */}
      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleSkipWelcomeModal}
        onComplete={handleCompleteWelcomeModal}
        userName={currentClinician?.name}
        onUpgrade={() => setShowUpgradeModal(true)}
      />

      {/* Welcome Tour */}
      <WelcomeTour
        isActive={showWelcomeTour}
        onComplete={handleCompleteTour}
        onSkip={handleSkipTour}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmLabel={t('common:buttons.delete')}
        cancelLabel={t('common:buttons.cancel')}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        variant="danger"
      />

      {/* Clinical Alerts Panel */}
      {showAlertsPanel && (
        <AlertsPanel
          alerts={alerts}
          onClose={() => setShowAlertsPanel(false)}
          onViewResult={(resultId) => {
            setShowAlertsPanel(false);
            setCurrentView({ type: 'VIEW_REPORT', resultId });
          }}
          onDismiss={handleDismissAlert}
          onDismissAll={handleDismissAllAlerts}
          onMarkAllRead={handleMarkAllAlertsRead}
        />
      )}

      {/* Battery Start Modal — patient selection before creating session */}
      {pendingBattery && currentClinician && (
        <BatteryStartModal
          battery={pendingBattery}
          clinicianId={currentClinician.id}
          onStart={(sessionId) => {
            setPendingBattery(null);
            setCurrentView({ type: 'BATTERY_RUNNER', sessionId });
          }}
          onClose={() => setPendingBattery(null)}
        />
      )}
    </div>
  );
}
